import DoneAllIcon from "@mui/icons-material/DoneAll";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorefrontIcon from "@mui/icons-material/Storefront";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import ProjectProcurementActivationApi from "../../../../../infrastructure/api/projectProcurementActivation";
import EquipmentPublicCodeChip from "../equipmentCatalog/EquipmentPublicCodeChip";
import { toJalaliFullDate } from "../../../../middleware/middleware";

const formatPrice = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("fa-IR");
};

const statusColor = (statusTitle) => {
  const text = String(statusTitle || "");
  if (text.includes("فعال") && !text.includes("غیر")) return "success";
  if (text.includes("معلق")) return "warning";
  if (text.includes("لغو")) return "error";
  if (text.includes("تکمیل")) return "info";
  if (text.includes("استعلام")) return "info";
  if (text.includes("انتخاب")) return "primary";
  if (text.includes("انتظار")) return "default";
  return "default";
};

const ProjectProcurementActivationPage = ({ source = "Management" }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requestId, setRequestId] = useState("8211");
  const [milestones, setMilestones] = useState([]);
  const [activations, setActivations] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [notes, setNotes] = useState("");
  const [estimatedAmount, setEstimatedAmount] = useState("");
  const [supplierInfoId, setSupplierInfoId] = useState("");
  const [procItems, setProcItems] = useState([]);

  const areaLabel = source === "Management" ? "مدیریت" : "اپراتور سولارنت";
  const basePath =
    source === "Management" ? "/SLN/Management" : "/SLN/SolarentOperator";

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    const rid = requestId.trim() ? Number(requestId.trim()) : null;
    const [msRes, actRes] = await Promise.all([
      ProjectProcurementActivationApi.getProcurementTriggerMilestones(source, rid),
      rid
        ? ProjectProcurementActivationApi.getProcurementActivations(source, { requestId: rid })
        : ProjectProcurementActivationApi.getProcurementActivations(source, {}),
    ]);
    if (!msRes.ok) {
      setError(msRes.message || "خطا در دریافت مایلستون‌های محرک تأمین");
      setMilestones([]);
    } else {
      setMilestones(Array.isArray(msRes.data) ? msRes.data : []);
    }
    if (!actRes.ok && !msRes.ok) {
      setActivations([]);
    } else if (!actRes.ok) {
      setError((prev) => prev || actRes.message || "خطا در دریافت فعال‌سازی‌های تأمین");
      setActivations([]);
    } else {
      setActivations(Array.isArray(actRes.data) ? actRes.data : []);
    }
    if (rid) {
      const itemsRes = await ProjectProcurementActivationApi.getProcurementRequestItems(source, rid);
      setProcItems(itemsRes.ok ? itemsRes.data || [] : []);
    } else {
      setProcItems([]);
    }
    setLoading(false);
  }, [requestId, source]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async (row) => {
    if (!row?.milestoneId || !row.canCreateProcurementActivation) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    const amount = estimatedAmount.trim() ? Number(estimatedAmount.trim()) : undefined;
    const res = await ProjectProcurementActivationApi.createProcurementActivation(source, {
      projectExecutionPlanMilestoneId: row.milestoneId,
      procurementType: row.suggestedProcurementType,
      estimatedAmount: Number.isFinite(amount) ? amount : undefined,
      notes: notes.trim() || undefined,
    });
    if (!res.ok) {
      setError(res.message || "ایجاد فعال‌سازی تأمین ناموفق بود.");
    } else {
      setSuccess(res.message || "فعال‌سازی تأمین ایجاد شد.");
      setNotes("");
      setEstimatedAmount("");
      await loadData();
    }
    setSubmitting(false);
  };

  const runAction = async (action, item, extra = {}) => {
    if (!item?.id) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    const payload = {
      projectProcurementActivationId: item.id,
      notes: notes.trim() || undefined,
      ...extra,
    };
    let res;
    if (action === "quotation") {
      res = await ProjectProcurementActivationApi.requestProcurementQuotation(source, payload);
    } else if (action === "supplier") {
      const sid = Number(supplierInfoId.trim() || item.supplierInfoId);
      if (!Number.isFinite(sid) || sid <= 0) {
        setError("شناسه تامین‌کننده معتبر نیست.");
        setSubmitting(false);
        return;
      }
      res = await ProjectProcurementActivationApi.selectProcurementSupplier(source, {
        projectProcurementActivationId: item.id,
        supplierInfoId: sid,
        notes: notes.trim() || undefined,
      });
    } else if (action === "suspend") {
      res = await ProjectProcurementActivationApi.suspendProcurementActivation(source, payload);
    } else if (action === "cancel") {
      res = await ProjectProcurementActivationApi.cancelProcurementActivation(source, payload);
    } else if (action === "complete") {
      res = await ProjectProcurementActivationApi.completeProcurementActivation(source, payload);
    }
    if (!res?.ok) {
      setError(res?.message || "عملیات ناموفق بود.");
    } else {
      setSuccess(res.message || "عملیات انجام شد.");
      setNotes("");
      await loadData();
    }
    setSubmitting(false);
  };

  return (
    <Box sx={{ p: 2 }}>
      <BreadcrumbsComponent
        items={[
          { title: areaLabel, href: basePath },
          { title: "فعال‌سازی تأمین تجهیزات" },
        ]}
      />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, mb: 2 }}>
        <Typography variant="h5">فعال‌سازی تأمین تجهیزات</Typography>
        <IconButton onClick={loadData} disabled={loading || submitting} aria-label="بارگذاری مجدد">
          <RefreshIcon />
        </IconButton>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="شناسه درخواست"
                value={requestId}
                onChange={(e) => setRequestId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="مبلغ تخمینی"
                value={estimatedAmount}
                onChange={(e) => setEstimatedAmount(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="شناسه تامین‌کننده"
                value={supplierInfoId}
                onChange={(e) => setSupplierInfoId(e.target.value)}
                helperText="برای انتخاب تامین‌کننده"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="یادداشت عملیات"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={loadData} disabled={loading}>
                اعمال فیلتر
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardHeader title="مایلستون‌های محرک تأمین" />
        <CardContent>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>مایلستون</TableCell>
                  <TableCell>نوع تأمین</TableCell>
                  <TableCell>طرف مسئول</TableCell>
                  <TableCell align="center">عملیات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {milestones.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      {loading ? "در حال بارگذاری..." : "مایلستونی یافت نشد."}
                    </TableCell>
                  </TableRow>
                ) : (
                  milestones.map((row) => (
                    <TableRow key={row.milestoneId}>
                      <TableCell>
                        <Typography variant="body2">{row.milestoneTitle}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{row.milestoneId}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.suggestedProcurementTypeTitle}</TableCell>
                      <TableCell>
                        {row.suggestedSupplierTitle || row.suggestedContractorTitle || row.responsiblePartyTypeTitle}
                      </TableCell>
                      <TableCell align="center">
                        {row.canCreateProcurementActivation ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PlayCircleOutlineIcon />}
                            disabled={submitting}
                            onClick={() => handleCreate(row)}
                          >
                            فعال‌سازی
                          </Button>
                        ) : (
                          <Chip size="small" color="info" label={`فعال #${row.existingProcurementActivationId}`} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="فعال‌سازی‌های تأمین ثبت‌شده" />
        <CardContent>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>مایلستون</TableCell>
                  <TableCell>نوع / وضعیت</TableCell>
                  <TableCell>تامین‌کننده / پیمانکار</TableCell>
                  <TableCell>مبلغ</TableCell>
                  <TableCell>تاریخ</TableCell>
                  <TableCell align="center">عملیات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {loading ? "در حال بارگذاری..." : "فعال‌سازی تأمینی ثبت نشده."}
                    </TableCell>
                  </TableRow>
                ) : (
                  activations.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2">{item.milestoneTitle}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{item.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Chip size="small" label={item.procurementTypeTitle} variant="outlined" />
                          <Chip
                            size="small"
                            color={statusColor(item.procurementActivationStatusTitle)}
                            label={item.procurementActivationStatusTitle}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.supplierTitle || "-"}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.contractorTitle || ""}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">تخمین: {formatPrice(item.estimatedAmount)}</Typography>
                        <Typography variant="caption">تایید: {formatPrice(item.approvedAmount)}</Typography>
                      </TableCell>
                      <TableCell>
                        {item.activatedAt ? toJalaliFullDate(item.activatedAt) : "-"}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                          {item.canRequestQuotation && (
                            <Button
                              size="small"
                              startIcon={<RequestQuoteIcon />}
                              disabled={submitting}
                              onClick={() => runAction("quotation", item)}
                            >
                              استعلام
                            </Button>
                          )}
                          {item.canSelectSupplier && (
                            <Button
                              size="small"
                              color="primary"
                              startIcon={<StorefrontIcon />}
                              disabled={submitting}
                              onClick={() => runAction("supplier", item)}
                            >
                              تامین‌کننده
                            </Button>
                          )}
                          {item.canSuspend && (
                            <Button
                              size="small"
                              color="warning"
                              startIcon={<PauseCircleOutlineIcon />}
                              disabled={submitting}
                              onClick={() => runAction("suspend", item)}
                            >
                              تعلیق
                            </Button>
                          )}
                          {item.canComplete && (
                            <Button
                              size="small"
                              color="success"
                              startIcon={<DoneAllIcon />}
                              disabled={submitting}
                              onClick={() => runAction("complete", item)}
                            >
                              تکمیل
                            </Button>
                          )}
                          {item.canCancel && (
                            <Button
                              size="small"
                              color="error"
                              disabled={submitting}
                              onClick={() => runAction("cancel", item)}
                            >
                              لغو
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </CardContent>
      </Card>

      {procItems.length > 0 ? (
        <Card sx={{ mt: 2 }}>
          <CardHeader title="اقلام درخواست تأمین (از اسنپ‌شات تجهیزات)" />
          <CardContent>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>کد</TableCell>
                  <TableCell>عنوان</TableCell>
                  <TableCell>برند</TableCell>
                  <TableCell>مدل</TableCell>
                  <TableCell>تعداد</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {procItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <EquipmentPublicCodeChip code={item.publicCode} />
                    </TableCell>
                    <TableCell>{item.title || item.equipmentType}</TableCell>
                    <TableCell>{item.brand}</TableCell>
                    <TableCell>{item.model}</TableCell>
                    <TableCell>
                      {item.quantity} {item.unit || ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}
    </Box>
  );
};

export default ProjectProcurementActivationPage;
